import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { AppLayout } from '@/components/ui/AppLayout';
import { PageHeader } from '@/components/ui/PageHeader';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input, Label, Select } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Banner } from '@/components/ui/Banner';
import { Badge } from '@/components/ui/StatusBadge';
import { Skeleton } from '@/components/ui/Skeleton';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useHotkey } from '@/hooks/useHotkey';
import { useAuthStore } from '@/stores/auth';
import {
  auditApi,
  type AuditLogDetail,
  type AuditLogEntry,
  type AuditFilters,
} from '@/services/audit.api';

/**
 * Page Audit (étape 17) - journal d'audit filtrable, accessible direction + admin.
 *
 * - La liste n'expose PAS `changes` (charge réduite + confidentialité).
 * - Clic sur une ligne → modale détail qui récupère le `changes` en plus.
 * - Filtres en ligne : user_id, action, entity_type, date range.
 */
export function AuditPage() {
  const token = useAuthStore((s) => s.accessToken)!;
  const user = useAuthStore((s) => s.user);
  const isAllowed = user?.role === 'admin' || user?.role === 'director';

  const [filters, setFilters] = useState<AuditFilters>({});
  const [items, setItems] = useState<AuditLogEntry[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<AuditLogEntry | null>(null);

  async function load(cursor?: string) {
    setLoading(true);
    setError(null);
    try {
      const res = await auditApi.list(token, { ...filters, cursor, limit: 50 });
      setItems((prev) => (cursor ? [...(prev ?? []), ...res.data] : res.data));
      setNextCursor(res.nextCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  if (!isAllowed) return <Navigate to="/" replace />;

  const columns: Column<AuditLogEntry>[] = [
    {
      key: 'createdAt',
      header: 'Date',
      width: '170px',
      render: (e) => new Date(e.createdAt).toLocaleString('fr-FR'),
    },
    {
      key: 'user',
      header: 'Utilisateur',
      render: (e) =>
        e.user ? (
          <div>
            <div className="font-medium text-brand">
              {e.user.firstName} {e.user.lastName}
            </div>
            <div className="text-xs text-text-muted">{e.user.email}</div>
          </div>
        ) : (
          <span className="text-text-muted">système</span>
        ),
    },
    {
      key: 'action',
      header: 'Action',
      render: (e) => <Badge tone="accent">{e.action}</Badge>,
    },
    {
      key: 'entityType',
      header: 'Entité',
      render: (e) => (
        <div>
          <div className="font-medium text-brand">{e.entityType}</div>
          {e.entityId && (
            <div className="font-mono text-[10px] text-text-muted">{e.entityId.slice(0, 8)}…</div>
          )}
        </div>
      ),
    },
    {
      key: 'ipAddress',
      header: 'IP',
      width: '140px',
      render: (e) => e.ipAddress ?? <span className="text-text-muted">-</span>,
    },
  ];

  return (
    <AppLayout>
      <PageHeader
        title="Journal d'audit"
        subtitle="Traçabilité des actions - direction + admin uniquement."
      />

      <FilterBar filters={filters} onChange={setFilters} />

      {error && (
        <div className="mb-4">
          <Banner tone="danger">{error}</Banner>
        </div>
      )}

      <DataTable
        columns={columns}
        data={items ?? []}
        keyExtractor={(e) => e.id}
        loading={items === null}
        onRowClick={(e) => setSelected(e)}
        empty={{ title: 'Aucune entrée', description: 'Essayez un autre filtre.' }}
      />

      {nextCursor && items && items.length > 0 && (
        <div className="mt-4 flex justify-center">
          <Button variant="secondary" loading={loading} onClick={() => void load(nextCursor)}>
            Charger plus
          </Button>
        </div>
      )}

      {selected && <AuditDetailModal entryId={selected.id} onClose={() => setSelected(null)} />}
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Filter bar
// ---------------------------------------------------------------------------

function FilterBar({
  filters,
  onChange,
}: {
  filters: AuditFilters;
  onChange: (f: AuditFilters) => void;
}) {
  const [draft, setDraft] = useState<AuditFilters>(filters);

  // Applique les filtres en différé (debounce manuel à la soumission - un
  // filtre par champ éviterait les re-fetch à chaque frappe).
  function apply() {
    onChange({ ...draft });
  }
  function reset() {
    setDraft({});
    onChange({});
  }

  return (
    <div className="mb-4 grid grid-cols-1 gap-3 rounded-md border border-border-subtle bg-white p-4 md:grid-cols-5">
      <div>
        <Label htmlFor="f-user">User ID</Label>
        <Input
          id="f-user"
          placeholder="UUID"
          value={draft.user_id ?? ''}
          onChange={(e) => setDraft((d) => ({ ...d, user_id: e.target.value || undefined }))}
        />
      </div>
      <div>
        <Label htmlFor="f-action">Action</Label>
        <Input
          id="f-action"
          placeholder="ex: create, update, login.success"
          value={draft.action ?? ''}
          onChange={(e) => setDraft((d) => ({ ...d, action: e.target.value || undefined }))}
        />
      </div>
      <div>
        <Label htmlFor="f-entity">Entité</Label>
        <Select
          id="f-entity"
          value={draft.entity_type ?? ''}
          onChange={(e) => setDraft((d) => ({ ...d, entity_type: e.target.value || undefined }))}
        >
          <option value="">Toutes</option>
          <option value="user">Utilisateur</option>
          <option value="prospect">Prospect</option>
          <option value="opportunity">Opportunité</option>
          <option value="quote">Devis</option>
          <option value="document">Document</option>
          <option value="event">Événement</option>
          <option value="sequence">Séquence</option>
          <option value="sequence_step">Étape séquence</option>
          <option value="prospect_sequence">Enrollment</option>
          <option value="contract_type">Type contrat</option>
          <option value="insurance_company">Compagnie</option>
          <option value="email_template">Template email</option>
          <option value="config_setting">Paramètre</option>
          <option value="session">Session</option>
        </Select>
      </div>
      <div>
        <Label htmlFor="f-from">Du</Label>
        <Input
          id="f-from"
          type="datetime-local"
          value={draft.date_from ? draft.date_from.slice(0, 16) : ''}
          onChange={(e) =>
            setDraft((d) => ({
              ...d,
              date_from: e.target.value ? new Date(e.target.value).toISOString() : undefined,
            }))
          }
        />
      </div>
      <div>
        <Label htmlFor="f-to">Au</Label>
        <Input
          id="f-to"
          type="datetime-local"
          value={draft.date_to ? draft.date_to.slice(0, 16) : ''}
          onChange={(e) =>
            setDraft((d) => ({
              ...d,
              date_to: e.target.value ? new Date(e.target.value).toISOString() : undefined,
            }))
          }
        />
      </div>
      <div className="md:col-span-5 flex justify-end gap-2">
        <Button variant="secondary" size="sm" onClick={reset}>
          Réinitialiser
        </Button>
        <Button variant="primary" size="sm" onClick={apply}>
          Appliquer
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail modal
// ---------------------------------------------------------------------------

function AuditDetailModal({ entryId, onClose }: { entryId: string; onClose: () => void }) {
  const token = useAuthStore((s) => s.accessToken)!;
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useFocusTrap(true, dialogRef);
  useHotkey('escape', onClose, { allowInInput: true });

  const [detail, setDetail] = useState<AuditLogDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    auditApi
      .get(token, entryId)
      .then(setDetail)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Erreur'));
  }, [token, entryId]);

  const changes = useMemo<Record<string, { old: unknown; new: unknown }> | null>(() => {
    if (!detail || typeof detail.changes !== 'object' || detail.changes === null) return null;
    return detail.changes as Record<string, { old: unknown; new: unknown }>;
  }, [detail]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-brand/30 px-4 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Détail audit"
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-modal animate-scale-in"
      >
        <div className="flex items-center justify-between border-b border-border-subtle px-6 py-4">
          <h2 className="text-lg font-semibold text-brand">Détail entrée d&apos;audit</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-text-muted hover:bg-gray-100 hover:text-brand"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {!detail && !error && <Skeleton className="h-40 w-full" />}
          {error && <Banner tone="danger">{error}</Banner>}
          {detail && (
            <>
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <Field label="Date" value={new Date(detail.createdAt).toLocaleString('fr-FR')} />
                <Field
                  label="Utilisateur"
                  value={
                    detail.user
                      ? `${detail.user.firstName} ${detail.user.lastName}`
                      : 'système / automation'
                  }
                />
                <Field label="Action" value={detail.action} />
                <Field label="Entité" value={detail.entityType} />
                {detail.entityId && (
                  <Field
                    label="Entity ID"
                    value={<code className="font-mono text-xs">{detail.entityId}</code>}
                  />
                )}
                {detail.ipAddress && <Field label="IP" value={detail.ipAddress} />}
              </dl>

              {detail.metadata && (
                <div>
                  <h3 className="mb-1 text-sm font-semibold text-brand">Metadata</h3>
                  <pre className="overflow-x-auto rounded-md bg-gray-50 p-3 text-xs">
                    {JSON.stringify(detail.metadata, null, 2)}
                  </pre>
                </div>
              )}

              {changes && Object.keys(changes).length > 0 && <ChangesDiff changes={changes} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-text-muted">{label}</dt>
      <dd className="mt-0.5 text-brand">{value}</dd>
    </div>
  );
}

function ChangesDiff({ changes }: { changes: Record<string, { old: unknown; new: unknown }> }) {
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold text-brand">Changements</h3>
      <div className="space-y-2">
        {Object.entries(changes).map(([field, diff]) => (
          <div
            key={field}
            className="grid grid-cols-[120px_1fr_1fr] gap-2 rounded-md border border-border-subtle p-2 text-xs"
          >
            <span className="font-mono text-text-muted">{field}</span>
            <DiffCell label="avant" tone="danger" value={diff?.old} />
            <DiffCell label="après" tone="success" value={diff?.new} />
          </div>
        ))}
      </div>
    </div>
  );
}

function DiffCell({
  label,
  tone,
  value,
}: {
  label: string;
  tone: 'danger' | 'success';
  value: unknown;
}) {
  const toneClass =
    tone === 'danger' ? 'bg-danger-50 text-danger-600' : 'bg-success-50 text-success-600';
  const rendered =
    value === null || value === undefined
      ? '-'
      : typeof value === 'object'
        ? JSON.stringify(value)
        : String(value);
  return (
    <div className={`rounded-md p-2 ${toneClass}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="mt-0.5 break-words font-mono">{rendered}</div>
    </div>
  );
}
